import React, { useEffect, useState } from 'react';
import { authAPI, adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaUserShield, FaCheckCircle, FaTimesCircle, FaTrash, FaSpinner, FaEnvelope } from 'react-icons/fa';
import { auth } from '../../services/firebase';
import { ROLES } from '../../config/constants';
import { useAuth } from '../../contexts/AuthContext';
import './AdminSettings.css';

function AdminSettings() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [processingUser, setProcessingUser] = useState(null);

    // Check if user is super admin
    const isSuperAdmin = currentUser?.role === ROLES.SUPER_ADMIN;

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            setErrorMessage(null);
            const response = await authAPI.getAllUsers();
            
            // Handle the response structure correctly
            if (!response || !response.users) {
                throw new Error('Invalid response format');
            }
            
            setUsers(response.users);
        } catch (error) {
            setError(`Failed to fetch users: ${error.message || 'Unknown error'}`);
            setErrorMessage(`Error: ${error.message || 'Unknown error'}. Please try reloading the page.`);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (uid, newRole) => {
        try {
            setProcessingUser(uid);
            setSuccessMessage(null);
            setErrorMessage(null);
            
            await adminAPI.updateUserRole(uid, newRole);
            
            await fetchUsers();
            setSuccessMessage(`User role updated successfully to ${newRole}`);
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setProcessingUser(null);
        }
    };

    const handleSendVerificationEmail = async (uid) => {
        try {
            setProcessingUser(uid);
            setSuccessMessage(null);
            setErrorMessage(null);
            
            await authAPI.sendVerificationEmail(uid);
            setSuccessMessage('Verification email sent successfully');
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setProcessingUser(null);
        }
    };

    const handleDeleteUser = async (uid) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }
        
        try {
            setProcessingUser(uid);
            setSuccessMessage(null);
            setErrorMessage(null);
            
            const result = await adminAPI.deleteUser(uid);
            
            await fetchUsers();
            setSuccessMessage('User deleted successfully');
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setProcessingUser(null);
        }
    };

    const filteredUsers = users.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderRoleBadge = (role) => {
        switch(role) {
            case ROLES.SUPER_ADMIN:
                return <span className="role-badge super-admin">Super Admin</span>;
            case ROLES.ADMIN:
                return <span className="role-badge admin">Admin</span>;
            case ROLES.EDITOR:
                return <span className="role-badge editor">Editor</span>;
            default:
                return <span className="role-badge user">User</span>;
        }
    };

    return (
        <div className="admin-settings">
            <div className="admin-settings-header">
                <button className="admin-back-button" onClick={() => navigate('/admin')}>
                    <FaArrowLeft /> Back to Dashboard
                </button>
                <div className="admin-header-content">
                    <h2 className="admin-header-title">User Management</h2>
                </div>
            </div>

            <div className="admin-header">
                <h2>Users & Permissions</h2>
                <p>Manage users and their roles across the platform</p>
            </div>

            <div className="search-container">
                <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search users by email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="user-count">
                    {filteredUsers.length} users found
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <FaSpinner className="spinner-icon" /> Loading users...
                </div>
            ) : error ? (
                <div className="error-display">
                    <FaTimesCircle /> {error}
                    <button 
                        className="retry-button"
                        onClick={fetchUsers}
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <div className="users-table-container">
                    {filteredUsers.length === 0 ? (
                        <div className="no-results">
                            <p>No users found matching your search criteria.</p>
                        </div>
                    ) : (
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th className="first-name-col">First Name</th>
                                    <th className="last-name-col">Last Name</th>
                                    <th>Role</th>
                                    <th>Email Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(user => (
                                    <tr key={user.uid} className={processingUser === user.uid ? 'processing-row' : ''}>
                                        <td>{user.email}</td>
                                        <td className="first-name-col">{user.firstName || ''}</td>
                                        <td className="last-name-col">{user.lastName || ''}</td>
                                        <td>
                                            {renderRoleBadge(user.role)}
                                            <select
                                                value={user.role || ROLES.USER}
                                                onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                                                className="role-select"
                                                disabled={processingUser === user.uid}
                                            >
                                                <option value={ROLES.USER}>User</option>
                                                <option value={ROLES.EDITOR}>Editor</option>
                                                <option value={ROLES.ADMIN}>Admin</option>
                                                <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
                                            </select>
                                        </td>
                                        <td>
                                            {user.emailVerified ? (
                                                <button
                                                    className="verify-button verified"
                                                    disabled={true}
                                                >
                                                    <FaCheckCircle /> Verified
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleSendVerificationEmail(user.uid)}
                                                    className="verify-button"
                                                    disabled={processingUser === user.uid}
                                                >
                                                    {processingUser === user.uid ? (
                                                        <>
                                                            <FaSpinner className="spinner-icon" /> Sending...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaEnvelope /> Send Verification Email
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteUser(user.uid)}
                                                className="delete-button"
                                                disabled={processingUser === user.uid || user.role === ROLES.SUPER_ADMIN}
                                                title={user.role === ROLES.SUPER_ADMIN ? "Super Admin users cannot be deleted" : "Delete user"}
                                            >
                                                <FaTrash /> {processingUser === user.uid ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminSettings;